/**
 * useStopCard — alle state, guards en handlers van een stopkaart.
 *
 * Gedeeld door de legacy-render (StopCard) en de Werkbank-render (StopCardWb),
 * zodat de twee kaarten niet binnen een week uit elkaar lopen. Hier leeft:
 * items laden, deels-opgehaald (≥2 kleden), betaling (lokale stand na een
 * transactie, overslaan, op-factuur), camera, bericht, item bewerken en het
 * afronden met de volgorde-/chauffeur-guard uit RouteDetailPage.
 *
 * Geen JSX. Wijzig gedrag hier, nooit in een van de renders.
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { salesforceClient } from '../../lib/salesforce';
import { TIMEOUTS, DEELS_AFGELEVERD, type DeelsAfgeleverdReden } from '../../lib/constants';
import { decodeHtmlEntities } from '../../lib/utils';
import type { StopCardProps, WasserijItemDto, WorkOrderDetailResponse } from './types';

/**
 * Stand van "we zijn eerder" op deze stop (plan 2026-10-12, Step 7b/9):
 *  - akkoord: klant zei ja → eerder mag;
 *  - nee: niet vóór het afgesproken venster;
 *  - plek: niet thuis, kleed ligt/mag op `tekst` (bij een onbetaalde
 *    aflevering wint de betaalpoort: niet neerleggen);
 *  - wacht: vraag gesteld, nog geen antwoord → niet vóór het venster;
 *  - null: niets aan de hand.
 */
export function vroegStand(stop: {
  Arrival_Time_D_t__c?: string;
  Tijd_vanaf__c?: string;
  vroegAkkoord?: 'ja' | 'nee' | 'plek' | null;
  vroegAkkoordTekst?: string | null;
  vroegGevraagdOp?: string | null;
}): { soort: 'akkoord' | 'nee' | 'plek' | 'wacht'; tekst: string } | null {
  const vanaf = stop.Tijd_vanaf__c ? stop.Tijd_vanaf__c.slice(0, 5) : null;
  if (stop.vroegAkkoord === 'ja') return { soort: 'akkoord', tekst: 'Eerder mag (klant akkoord)' };
  if (stop.vroegAkkoord === 'nee') return { soort: 'nee', tekst: `Niet vóór ${vanaf ?? 'het afgesproken tijdvak'} (klant)` };
  if (stop.vroegAkkoord === 'plek') return { soort: 'plek', tekst: `Niet thuis, kleed: ${stop.vroegAkkoordTekst || 'zie opmerking'}` };
  if (stop.vroegGevraagdOp) return { soort: 'wacht', tekst: `Niet vóór ${vanaf ?? 'het tijdvak'}: klant is gevraagd, nog geen antwoord` };
  return null;
}

export function useStopCard(props: StopCardProps) {
  const {
    stop,
    onComplete,
    onPartialPickup,
    initiallyExpanded = false,
    onPaymentUpdated,
    checkCanComplete,
    onShowSequenceWarning,
    onShowChauffeurBlock,
  } = props;

  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [items, setItems] = useState<WasserijItemDto[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);

  // Edit item state (for pickup stops only)
  const [editingItem, setEditingItem] = useState<WasserijItemDto | null>(null);

  // Deels opgehaald (partial pickup) modal state
  const [showPartialModal, setShowPartialModal] = useState(false);
  const [partialSelected, setPartialSelected] = useState<Set<string>>(new Set());
  const [partialNotes, setPartialNotes] = useState('');
  const [partialReason, setPartialReason] = useState<DeelsAfgeleverdReden | null>(null);

  // Message modal state
  const [showMessageModal, setShowMessageModal] = useState(false);

  // Media-laag camera (MEDIA_EIGEN) — foto op WO-niveau, fase ophaal/aflevering
  const [showCamera, setShowCamera] = useState(false);

  // Local payment state (updated after successful payment)
  const [localTotalTransactions, setLocalTotalTransactions] = useState(stop.totalTransactions ?? 0);
  const [localOpenAmount, setLocalOpenAmount] = useState(stop.openAmount ?? stop.totalInclBtw ?? 0);
  const [localIsFullyPaid, setLocalIsFullyPaid] = useState(stop.isFullyPaid ?? false);
  const [paymentSkipped, setPaymentSkipped] = useState(false);

  // Treat as fully paid if openAmount <= 0 (including overpayment)
  const effectivelyFullyPaid = localIsFullyPaid || localOpenAmount <= 0;

  // Sync local payment state when stop prop changes (after refresh)
  useEffect(() => {
    setLocalTotalTransactions(stop.totalTransactions ?? 0);
    setLocalOpenAmount(stop.openAmount ?? stop.totalInclBtw ?? 0);
    setLocalIsFullyPaid(stop.isFullyPaid ?? false);
  }, [stop.totalTransactions, stop.openAmount, stop.isFullyPaid, stop.totalInclBtw]);

  // Debug: log stop data
  useEffect(() => {
    console.log('[StopCard] Stop data:', stop);
  }, [stop]);

  // Load work order items immediately (for summary display)
  useEffect(() => {
    async function loadItems() {
      if (!stop.Werk_Order__c || items.length > 0) return;

      setIsLoadingItems(true);
      setItemsError(null);

      try {
        const response = await salesforceClient.callApexRest<{ success: boolean; data?: WorkOrderDetailResponse }>(
          `/v1/field/workorders/${stop.Werk_Order__c}`
        );

        console.log('[StopCard] Work order detail response:', response);

        if (response.success && response.data?.items) {
          setItems(response.data.items);
        } else {
          setItemsError('Kon items niet laden');
        }
      } catch (err) {
        console.error('[StopCard] Error loading items:', err);
        setItemsError('Fout bij laden items');
      } finally {
        setIsLoadingItems(false);
      }
    }

    loadItems();
  }, [isExpanded, stop.Werk_Order__c, items.length]);

  // Function to reload items after editing
  const reloadItems = useCallback(async () => {
    if (!stop.Werk_Order__c) return;

    setIsLoadingItems(true);
    try {
      const response = await salesforceClient.callApexRest<{ success: boolean; data?: WorkOrderDetailResponse }>(
        `/v1/field/workorders/${stop.Werk_Order__c}`
      );

      if (response.success && response.data?.items) {
        setItems(response.data.items);
      }
    } catch (err) {
      console.error('[StopCard] Error reloading items:', err);
    } finally {
      setIsLoadingItems(false);
    }
  }, [stop.Werk_Order__c]);

  // =============================================================================
  // DISPLAY VALUES
  // =============================================================================

  const displayCustomerName = stop.customerName || stop.Werk_Order__r?.Klantnaam__c || stop.Werk_Order__r?.Naam_op_Factuur__c || 'Onbekende klant';
  const displayContactName = stop.contactName && stop.contactName !== displayCustomerName ? stop.contactName : undefined;
  const rawAddress = stop.address || stop.Address__c ||
    (stop.Type__c === 'Ophalen' ? stop.Werk_Order__r?.Ophaaladres_formule__c : stop.Werk_Order__r?.Afleveradres_formule__c) ||
    'Geen adres';
  const displayAddress = decodeHtmlEntities(rawAddress)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\s+/g, ' ')
    .trim();
  const addressParts = displayAddress.match(/^(.+?)\s+(\d{4}\s*[A-Z]{2}\s+.+)$/i);
  const formattedAddress = addressParts
    ? `${addressParts[1]}\n${addressParts[2]}`
    : displayAddress;
  /** Straat + huisnummer (eerste regel), voor de NextStopBar. */
  const streetLine = addressParts?.[1] ?? displayAddress;
  const displayWorkOrderName = stop.workOrderName || stop.Werk_Order__r?.Name || stop.Name;
  const displayPhoneNumber = stop.phoneNumber || stop.Werk_Order__r?.Mobiel__c || stop.Werk_Order__r?.Telefoon__c;
  const displayTimeWindow = stop.timeWindow || (
    stop.Tijd_vanaf__c && stop.Tijd_tot__c
      ? `${String(stop.Tijd_vanaf__c).substring(0, 5)} - ${String(stop.Tijd_tot__c).substring(0, 5)}`
      : null
  );

  // Calculate item stats from loaded items
  // Items to display: Primary items OR any item with klaarleggen=true (e.g., ondertapijten)
  const displayItems = items.filter(item =>
    (item.productCategory || 'Primary') === 'Primary' || item.klaarleggen === true
  );
  const totalItemCount = displayItems.length;

  // Na-ophaal: bij een ophaalstop betekent een ingevulde Status__c dat het kleed al
  // eerder is opgehaald (ligt al in de was). Die hoeft de chauffeur niet opnieuw mee te
  // nemen — markeren + dimmen, zodat duidelijk is wat er nog op te halen is.
  const isPickupStop = stop.Type__c === 'Ophalen';
  const alreadyPickedUpCount = isPickupStop
    ? displayItems.filter(item => !!item.status).length
    : 0;
  const toPickUpCount = totalItemCount - alreadyPickedUpCount;
  // Nalevering: bij een afleverstop is een kleed met status 'Afgeleverd' op een
  // eerdere rit al bij de klant bezorgd. Alleen de achterblijver gaat nu mee —
  // markeren + dimmen, spiegel van "Al opgehaald".
  const isDeliveryStop = stop.Type__c === 'Terugbrengen';
  const alreadyDeliveredCount = isDeliveryStop
    ? displayItems.filter(item => item.status === 'Afgeleverd').length
    : 0;
  const toDeliverCount = totalItemCount - alreadyDeliveredCount;

  // Kleden die nu mee kunnen: ophalen = status nog leeg, afleveren = nog niet
  // 'Afgeleverd'. Zelfde lijst voedt de deels-modal in beide richtingen.
  const partialItems = useMemo(
    () =>
      isPickupStop
        ? displayItems.filter(item => !item.status)
        : isDeliveryStop
          ? displayItems.filter(item => item.status !== 'Afgeleverd')
          : [],
    [displayItems, isPickupStop, isDeliveryStop]
  );
  // "Deels …" is alleen zinvol bij ≥2 kleden; de aflever-kant staat achter een flag.
  const canPartialPickup =
    !!onPartialPickup && partialItems.length >= 2 && (isPickupStop || (isDeliveryStop && DEELS_AFGELEVERD));
  /** Teksten van de deels-modal en -knop volgen de richting van de stop. */
  const partialRichting = isDeliveryStop ? ('afleveren' as const) : ('ophalen' as const);

  const openPartialModal = useCallback(() => {
    // Standaard: alle kleden aangevinkt (meegenomen); chauffeur vinkt de
    // achtergebleven kleden uit.
    setPartialSelected(new Set(partialItems.map(i => i.itemId)));
    setPartialNotes('');
    setPartialReason(null);
    setShowPartialModal(true);
  }, [partialItems]);

  const togglePartialItem = useCallback((itemId: string) => {
    setPartialSelected(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  // Afleveren vraagt een vaste reden ('Anders' met toelichting); ophalen niet.
  const partialReasonOk =
    partialRichting !== 'afleveren' || (!!partialReason && (partialReason !== 'Anders' || partialNotes.trim().length > 0));

  const confirmPartialPickup = useCallback(() => {
    if (!onPartialPickup || partialSelected.size === 0 || !partialReasonOk) return;
    onPartialPickup(
      Array.from(partialSelected),
      partialNotes.trim() || undefined,
      partialRichting === 'afleveren' && partialReason ? partialReason : undefined
    );
    setShowPartialModal(false);
  }, [onPartialPickup, partialSelected, partialNotes, partialReason, partialReasonOk, partialRichting]);

  const largestRug = displayItems.reduce((largest, item) => {
    const currentArea = item.surfaceM2 || 0;
    const largestArea = largest?.surfaceM2 || 0;
    return currentArea > largestArea ? item : largest;
  }, null as WasserijItemDto | null);

  const longestSideCm = displayItems.reduce((longest, item) => {
    const itemLongestSide = Math.max(item.lengthCm || 0, item.widthCm || 0);
    return itemLongestSide > longest ? itemLongestSide : longest;
  }, 0);

  // Check if this is a delivery stop with payment info
  const hasPaymentInfo = stop.totalInclBtw != null && stop.totalInclBtw > 0;
  // Klant betaalt achteraf op factuur: chauffeur mag NIET innen (geen pin/contant/QR).
  const opFactuur = stop.opFactuur === true;
  // Betaling is hier "afgehandeld" als volledig betaald, overgeslagen, of op-factuur.
  const paymentSettled = effectivelyFullyPaid || paymentSkipped || opFactuur;
  // De afrond-knop mag pas als de betaling is afgehandeld (of het geen afleverstop met geld is).
  const canShowCompleteActions = isDeliveryStop && hasPaymentInfo ? paymentSettled : true;

  /** Tekst op de primaire knop; beide renders gebruiken dezelfde. */
  const primaryLabel = isPickupStop && totalItemCount > 0
    ? `${totalItemCount} ${totalItemCount === 1 ? 'item' : 'items'} opgehaald`
    : 'Voltooien';

  // =============================================================================
  // PAYMENT HANDLERS
  // =============================================================================

  const handlePaymentSuccess = useCallback((result: {
    totalTransactions: number;
    openAmount: number;
    isFullyPaid: boolean;
  }) => {
    setLocalTotalTransactions(result.totalTransactions);
    setLocalOpenAmount(result.openAmount);
    setLocalIsFullyPaid(result.isFullyPaid);

    if (onPaymentUpdated) {
      onPaymentUpdated({
        totalTransactions: result.totalTransactions,
        openAmount: result.openAmount,
        isFullyPaid: result.isFullyPaid,
      });
    }
  }, [onPaymentUpdated]);

  // =============================================================================
  // COMPLETE HANDLER
  // =============================================================================

  const handleCompleteWithAnimation = useCallback(() => {
    console.log('[StopCard] handleCompleteWithAnimation called for stop:', stop.Id);

    // Check if we can complete BEFORE starting animation
    if (checkCanComplete) {
      const result = checkCanComplete();
      if (result === 'chauffeur_mismatch') {
        onShowChauffeurBlock?.();
        return;
      }
      if (result === 'out_of_sequence') {
        onShowSequenceWarning?.();
        return;
      }
    }

    // Now we can safely animate and complete
    setIsCompleting(true);

    // Trigger haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(TIMEOUTS.HAPTIC_FEEDBACK_MS);
    }

    // Wait for animation, then complete
    setTimeout(() => {
      onComplete();

      // Scroll to next card after state updates
      setTimeout(() => {
        const nextCard = cardRef.current?.parentElement?.querySelector('[data-stop-card]:not([data-completing="true"])');
        if (nextCard) {
          nextCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, TIMEOUTS.SCROLL_DELAY_MS);
    }, TIMEOUTS.CARD_COMPLETE_ANIMATION_MS);
  }, [stop.Id, checkCanComplete, onShowChauffeurBlock, onShowSequenceWarning, onComplete]);

  return {
    cardRef,
    // expand/collapse
    isExpanded, setIsExpanded,
    // items
    items, isLoadingItems, itemsError, reloadItems,
    displayItems, totalItemCount,
    isPickupStop, alreadyPickedUpCount, toPickUpCount, partialItems, partialRichting, canPartialPickup,
    isDeliveryStop, alreadyDeliveredCount, toDeliverCount,
    largestRug, longestSideCm,
    // display
    displayCustomerName, displayContactName, displayAddress, formattedAddress, streetLine,
    displayWorkOrderName, displayPhoneNumber, displayTimeWindow,
    // completing
    isCompleting, handleCompleteWithAnimation, canShowCompleteActions, primaryLabel,
    // payment
    hasPaymentInfo, opFactuur, paymentSettled, effectivelyFullyPaid,
    localTotalTransactions, localOpenAmount, paymentSkipped, setPaymentSkipped,
    showPaymentModal, setShowPaymentModal, showQrModal, setShowQrModal, handlePaymentSuccess,
    // edit item
    editingItem, setEditingItem,
    // partial pickup
    showPartialModal, setShowPartialModal, partialSelected, partialNotes, setPartialNotes,
    openPartialModal, togglePartialItem, confirmPartialPickup, partialReason, setPartialReason, partialReasonOk,
    // message + camera
    showMessageModal, setShowMessageModal, showCamera, setShowCamera,
  };
}

export type StopCardState = ReturnType<typeof useStopCard>;
